import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Lock, ChevronLeft, Loader2, Download, FileText, 
  Map, ClipboardCheck, Target, ExternalLink, Crown,
  RefreshCw, Award, FileCheck, Briefcase, MessageSquare,
  AlertCircle, Settings, CreditCard, HelpCircle, ChevronDown, ChevronUp,
  Headphones, Users
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../services/api';
import { toast } from 'sonner';

const ICON_MAP = {
  FileText: FileText,
  Map: Map,
  ClipboardCheck: ClipboardCheck,
  Target: Target,
  RefreshCw: RefreshCw,
  Award: Award,
  FileCheck: FileCheck,
  Briefcase: Briefcase
};

const CATEGORY_CONFIG = {
  documents: { label: 'Documents', color: '#F97316' },
  playbooks: { label: 'Playbooks', color: '#22C55E' },
  worksheets: { label: 'Worksheets', color: '#3B82F6' },
  agency: { label: 'The Agency & Advocacy', color: '#D4AF37' }
};

// Agency & Advocacy Services - Premium services for Elite tier
const AGENCY_SERVICES = [
  {
    id: 'reclass_audit',
    title: 'Strategic Reclassification Audit',
    description: 'A deep-dive eligibility and roadmap session for athletes considering repeating a grade for athletic or academic advantages.',
    icon: 'RefreshCw',
    type: 'premium',
    buttonText: 'Contact Coach',
    requiresElite: true
  },
  {
    id: 'recommendation_letter',
    title: 'Letters of Recommendation',
    description: 'Formal recommendation letters from the Academy. Available after completing at least 30 days in The Combine.',
    icon: 'Award',
    type: 'request',
    buttonText: 'Request Letter',
    requiresDays: 30
  },
  {
    id: 'placement_letter',
    title: 'Placement Letters',
    description: 'Official placement letters for students transferring to private schools, prep schools, or academic institutions.',
    icon: 'FileCheck',
    type: 'request',
    buttonText: 'Request Placement Letter',
    requiresElite: true
  },
  {
    id: 'performance_portfolio',
    title: 'The Performance Portfolio',
    description: 'A consolidated PDF of all Scouting Reports and IXL milestones, professionally formatted for school admissions officers.',
    icon: 'Briefcase',
    type: 'generate',
    buttonText: 'Generate Portfolio',
    requiresElite: true
  }
];

// FAQ Data
const FAQ_DATA = [
  {
    question: 'What is the "Commitment Box"?',
    answer: 'Every athlete must sign their Letter of Intent (LOI) and upload a photo of the signed document to the dashboard. This "Gate" ensures that our students are mentally committed to the work before the academic training begins.'
  },
  {
    question: 'Can I change my tier later?',
    answer: 'Yes. You can upgrade from the Baseline to the Elite Pivot at any time via your Member Dashboard to unlock immediate access to On-Demand Transcripts and Reclassification Audits.'
  },
  {
    question: 'How does the IXL integration work?',
    answer: 'Once enrolled, your student will be assigned a roster spot. We use IXL\'s real-time diagnostics to power our "Scouting Reports," allowing Coach to see exactly where the "game film" shows a need for improvement.'
  },
  {
    question: 'What is "Reclassification Assistance"?',
    answer: 'For families considering having their athlete repeat a grade for academic or athletic advantage, we provide a Strategic Audit. We review eligibility, state requirements, and credit alignment to ensure a seamless transition.'
  },
  {
    question: 'How do I get my official Academy Transcript?',
    answer: 'Elite Pivot members can click "Request Transcript" in their dashboard. Our team will generate a professionalized scholastic portfolio that highlights both IXL mastery and Academy milestones, perfect for prep school or college admissions.'
  }
];

const TheVault = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [userTier, setUserTier] = useState(null);
  const [daysEnrolled, setDaysEnrolled] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactType, setContactType] = useState('support'); // 'support' or 'coach'
  const [contactForm, setContactForm] = useState({ name: '', grade: '', sport: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);

  useEffect(() => {
    loadVault();
  }, []);

  const loadVault = async () => {
    const token = localStorage.getItem('academy_token');
    if (!token) {
      toast.error('Please log in');
      navigate('/academy/enroll');
      return;
    }

    try {
      const response = await api.get('/academy/lms/vault', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setResources(response.data.resources || []);
        setByCategory(response.data.by_category || {});
        setUserTier(response.data.user_tier || 'baseline');
        setDaysEnrolled(response.data.days_enrolled || 0);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Please complete enrollment');
        navigate('/academy/enroll');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (resource) => {
    window.open(resource.file_url, '_blank');
  };

  const isEliteTier = userTier === 'elite_pivot' || userTier === 'elite';

  const handleAgencyServiceClick = (service) => {
    // Check if user meets requirements
    if (service.requiresElite && !isEliteTier) {
      setShowUpgradeModal(true);
      return;
    }
    
    if (service.requiresDays && daysEnrolled < service.requiresDays) {
      toast.error(`This service requires at least ${service.requiresDays} days in the program. You have ${daysEnrolled} days.`);
      return;
    }

    // Handle different service types
    if (service.type === 'premium') {
      // Contact coach - open email or show contact modal
      window.location.href = 'mailto:info@prsvr.com?subject=Strategic Reclassification Audit Request';
      toast.success('Opening email to contact your coach');
    } else if (service.type === 'request' || service.type === 'generate') {
      setSelectedService(service);
      setShowRequestModal(true);
    }
  };

  const handleSubmitRequest = async () => {
    if (!selectedService) return;
    
    setRequestLoading(true);
    try {
      const token = localStorage.getItem('academy_token');
      const response = await api.post('/academy/agency/request', {
        service_id: selectedService.id,
        service_title: selectedService.title
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Request submitted! Your coach will follow up within 48 hours.');
        setShowRequestModal(false);
        setSelectedService(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setRequestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate('/academy/high-performance')}
            variant="ghost"
            className="text-gray-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Dashboard
          </Button>
        </div>

        {/* Title Section */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-14 h-14 bg-orange-500/20 rounded-lg flex items-center justify-center border border-orange-500/30">
              <Lock className="w-7 h-7 text-orange-500" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight">
                THE <span className="text-orange-500">VAULT</span>
              </h1>
              <p className="text-gray-500 text-sm uppercase tracking-widest">Downloadable Resources</p>
            </div>
          </div>
          <p className="text-gray-400 mt-4 max-w-2xl">
            Your exclusive collection of playbooks, worksheets, and documents. Download and use these resources to support your training.
          </p>
        </div>

        {/* Resources by Category */}
        {Object.entries(byCategory).map(([category, categoryResources]) => {
          const config = CATEGORY_CONFIG[category] || { label: category, color: '#888' };
          
          return (
            <div key={category} className="mb-10">
              <h2 
                className="text-lg font-black uppercase tracking-wider mb-4 flex items-center gap-2"
                style={{ color: config.color }}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }}></span>
                {config.label}
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                {categoryResources.map((resource) => {
                  const IconComponent = ICON_MAP[resource.icon] || FileText;
                  
                  return (
                    <Card 
                      key={resource.id}
                      className="bg-gray-900 border-gray-800 hover:border-orange-500/50 transition-all group"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          {/* Icon */}
                          <div 
                            className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                            style={{ backgroundColor: `${config.color}20` }}
                          >
                            <IconComponent className="w-6 h-6" style={{ color: config.color }} />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1">
                            <h3 className="text-white font-bold text-lg mb-1">{resource.title}</h3>
                            <p className="text-gray-400 text-sm mb-4">{resource.description}</p>
                            
                            <div className="flex items-center gap-3">
                              <Button
                                onClick={() => handleDownload(resource)}
                                size="sm"
                                className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                {resource.file_type === 'pdf' ? 'Download PDF' : 'View File'}
                              </Button>
                              
                              {resource.generated_per_student && (
                                <span className="text-xs text-gray-500 italic">
                                  * Generated for you
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* ==================== THE AGENCY & ADVOCACY SECTION ==================== */}
        <div className="mb-10 mt-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center border border-amber-500/30">
              <Crown className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-amber-400 tracking-tight">
                THE AGENCY & ADVOCACY
              </h2>
              <p className="text-gray-500 text-sm uppercase tracking-widest">Premium Services</p>
            </div>
          </div>
          
          <p className="text-gray-400 mb-6 max-w-2xl">
            High-touch advocacy services for families seeking strategic academic support, placement assistance, and official documentation.
          </p>

          {/* Elite tier banner */}
          {!isEliteTier && (
            <div className="mb-6 p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-400 font-semibold text-sm">Elite Pivot Required</p>
                <p className="text-gray-400 text-sm">
                  Most Agency services are exclusive to our Elite Pivot members. 
                  <button 
                    onClick={() => navigate('/academy/enroll')}
                    className="text-amber-400 hover:underline ml-1"
                  >
                    Upgrade to unlock full advocacy access →
                  </button>
                </p>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {AGENCY_SERVICES.map((service) => {
              const IconComponent = ICON_MAP[service.icon] || FileText;
              const isLocked = service.requiresElite && !isEliteTier;
              const needsMoreDays = service.requiresDays && daysEnrolled < service.requiresDays;
              
              return (
                <Card 
                  key={service.id}
                  className={`bg-gray-900 border-gray-800 transition-all group relative overflow-hidden ${
                    isLocked ? 'opacity-75' : 'hover:border-amber-500/50'
                  }`}
                >
                  {isLocked && (
                    <div className="absolute top-3 right-3">
                      <Lock className="w-4 h-4 text-amber-500" />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div 
                        className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform ${
                          !isLocked ? 'group-hover:scale-110' : ''
                        }`}
                        style={{ backgroundColor: isLocked ? '#888888' + '20' : '#D4AF37' + '20' }}
                      >
                        <IconComponent 
                          className="w-6 h-6" 
                          style={{ color: isLocked ? '#888888' : '#D4AF37' }} 
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-lg mb-1">{service.title}</h3>
                        <p className="text-gray-400 text-sm mb-4">{service.description}</p>
                        
                        <Button
                          onClick={() => handleAgencyServiceClick(service)}
                          size="sm"
                          className={`font-bold ${
                            isLocked 
                              ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                              : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black'
                          }`}
                        >
                          {isLocked ? (
                            <>
                              <Lock className="w-4 h-4 mr-2" />
                              Upgrade to Elite
                            </>
                          ) : needsMoreDays ? (
                            <>
                              <Lock className="w-4 h-4 mr-2" />
                              {service.requiresDays - daysEnrolled} Days Remaining
                            </>
                          ) : (
                            <>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              {service.buttonText}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Empty State */}
        {resources.length === 0 && (
          <div className="py-20 text-center">
            <Lock className="w-20 h-20 text-gray-700 mx-auto mb-6" />
            <h3 className="text-xl font-bold text-white mb-2">The Vault is Empty</h3>
            <p className="text-gray-500">Resources will appear here as you progress through the program.</p>
          </div>
        )}

        {/* Footer Note */}
        <div className="mt-12 p-6 bg-gray-900/50 border border-gray-800 rounded-lg text-center">
          <p className="text-gray-500 text-sm">
            All resources are exclusive to enrolled Scholar Athletes. 
            <br />
            For additional materials, contact your coach at <a href="mailto:info@prsvr.com" className="text-orange-500 hover:underline">info@prsvr.com</a>
          </p>
        </div>

        {/* ==================== FAQ SECTION ==================== */}
        <div className="mt-16 mb-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-500/30">
              <HelpCircle className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-blue-400 tracking-tight">
                FAQ: THE COMBINE & ADVOCACY
              </h2>
              <p className="text-gray-500 text-sm uppercase tracking-widest">Common Questions</p>
            </div>
          </div>

          <div className="space-y-3">
            {FAQ_DATA.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/50"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-800/50 transition-colors"
                >
                  <span className="font-bold text-white pr-4">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-4 pb-4 text-gray-400 text-sm leading-relaxed border-t border-gray-800 pt-3 animate-in slide-in-from-top-2 duration-200">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ==================== CONTACT THE FRONT OFFICE ==================== */}
        <div className="mt-10 mb-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center border border-green-500/30">
              <Users className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-green-400 tracking-tight">
                CONTACT THE FRONT OFFICE
              </h2>
              <p className="text-gray-500 text-sm uppercase tracking-widest">We're Here to Help</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Technical Support Card */}
            <Card 
              className="bg-gray-900 border-gray-800 hover:border-blue-500/50 transition-all cursor-pointer group"
              onClick={() => {
                setContactType('support');
                setShowContactModal(true);
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Headphones className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">Technical Support</h3>
                    <p className="text-gray-400 text-sm mb-3">For login issues, billing questions, or platform troubleshooting.</p>
                    <span className="text-blue-400 text-sm font-semibold flex items-center gap-1">
                      Get Help <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Speak with Coach Card */}
            <Card 
              className="bg-gray-900 border-gray-800 hover:border-orange-500/50 transition-all cursor-pointer group"
              onClick={() => {
                setContactType('coach');
                setShowContactModal(true);
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Award className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">Speak with Coach</h3>
                    <p className="text-gray-400 text-sm mb-3">For academic strategy, advocacy questions, or program guidance.</p>
                    <span className="text-orange-400 text-sm font-semibold flex items-center gap-1">
                      Schedule Call <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ==================== ACCOUNT & SETTINGS SECTION ==================== */}
        <div className="mt-10 pt-8 border-t border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="w-5 h-5 text-gray-500" />
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">
              Account & Settings
            </h2>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => window.open('https://billing.stripe.com/p/login/00w00j2HvfnRa2KbJI48000', '_blank')}
              variant="outline"
              className="border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white hover:border-gray-600 transition-all"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Manage Membership & Billing
              <ExternalLink className="w-3 h-3 ml-2 opacity-50" />
            </Button>
            
            <Button
              onClick={() => window.location.href = 'mailto:info@prsvr.com?subject=Support Request'}
              variant="outline"
              className="border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white hover:border-gray-600 transition-all"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-center flex items-center justify-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" />
              Upgrade to Elite Pivot
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 text-center">
            <p className="text-gray-400 mb-6">
              The Agency & Advocacy services are exclusive to our <span className="text-amber-400 font-bold">Elite Pivot</span> members. 
              Upgrade to unlock:
            </p>
            
            <ul className="text-left space-y-2 mb-6 px-4">
              <li className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-amber-500">✓</span>
                On-Demand Scholastic Transcripts
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-amber-500">✓</span>
                Transfer & Transition Assistance
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-amber-500">✓</span>
                Strategic Reclassification Audits
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-amber-500">✓</span>
                Performance Portfolio Generation
              </li>
            </ul>
            
            <Button
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/academy/enroll');
              }}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold py-6"
            >
              View Elite Pivot Plan
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Service Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-center">
              {selectedService?.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-400 text-sm mb-6 text-center">
              {selectedService?.type === 'generate' 
                ? 'This will generate your Performance Portfolio based on your progress data.'
                : 'Your request will be reviewed by your coach and processed within 48 hours.'}
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setShowRequestModal(false)}
                variant="outline"
                className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitRequest}
                disabled={requestLoading}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold"
              >
                {requestLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  selectedService?.type === 'generate' ? 'Generate Now' : 'Submit Request'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Front Office Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-center flex items-center justify-center gap-2">
              {contactType === 'support' ? (
                <>
                  <Headphones className="w-6 h-6 text-blue-400" />
                  Technical Support
                </>
              ) : (
                <>
                  <Award className="w-6 h-6 text-orange-400" />
                  Speak with Coach
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              const subject = contactType === 'support' 
                ? 'Technical Support Request' 
                : 'Request to Speak with Coach';
              const body = `Student Name: ${contactForm.name}%0D%0ACurrent Grade: ${contactForm.grade}%0D%0ASport: ${contactForm.sport}%0D%0A%0D%0AMessage:%0D%0A${contactForm.message}`;
              window.location.href = `mailto:info@prsvr.com?subject=${subject}&body=${body}`;
              setShowContactModal(false);
              setContactForm({ name: '', grade: '', sport: '', message: '' });
            }}
            className="py-4 space-y-4"
          >
            <p className="text-gray-400 text-sm text-center mb-4">
              {contactType === 'support' 
                ? 'Having trouble with the platform? We\'ll get you back in the game.'
                : 'Ready to discuss academic strategy or advocacy options? Let\'s connect.'}
            </p>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Student Name</label>
              <input
                type="text"
                required
                value={contactForm.name}
                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                placeholder="Full name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Current Grade</label>
                <select
                  required
                  value={contactForm.grade}
                  onChange={(e) => setContactForm(prev => ({ ...prev, grade: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                >
                  <option value="">Select...</option>
                  <option value="6th">6th Grade</option>
                  <option value="7th">7th Grade</option>
                  <option value="8th">8th Grade</option>
                  <option value="9th">9th Grade</option>
                  <option value="10th">10th Grade</option>
                  <option value="11th">11th Grade</option>
                  <option value="12th">12th Grade</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Sport</label>
                <input
                  type="text"
                  required
                  value={contactForm.sport}
                  onChange={(e) => setContactForm(prev => ({ ...prev, sport: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                  placeholder="e.g. Basketball"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">
                {contactType === 'support' ? 'Describe Your Issue' : 'What would you like to discuss?'}
              </label>
              <textarea
                required
                value={contactForm.message}
                onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                rows={3}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500 resize-none"
                placeholder={contactType === 'support' 
                  ? 'Please describe the issue you\'re experiencing...'
                  : 'Questions about transcripts, reclassification, or program strategy...'}
              />
            </div>
            
            <Button
              type="submit"
              className={`w-full font-bold py-6 ${
                contactType === 'support'
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-orange-500 hover:bg-orange-600 text-black'
              }`}
            >
              {contactType === 'support' ? 'Submit Support Request' : 'Request Callback'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TheVault;
